import type { ComponentType } from "react";
import { useEffect } from "react";
import { lazyWithPreload } from "@/utils/lazy-with-preload";

// The start page sits behind three lazy steps: the app route, the index route and the page itself. React 19 holds a
// Suspense reveal until 300 ms after the last fallback, so the app shell appears 300 ms after the entry's loading
// screen. Whatever has not loaded by then gets its own fallback and another 300 ms, which delayed the start page's
// queries by that much. On the start page, the entry starts the page's chunk and the app route makes the index route
// wait for it.
export const StartPageRoute = lazyWithPreload(() =>
  import("@/routes/start-page-route").then(
    (m): { default: ComponentType<object> } => ({ default: m.StartPageRoute }),
  ),
);

export const opensStartPage = () =>
  /^\/(company\/[^/]+\/)?(home\/?)?$/.test(window.location.pathname);

// A recruitment's page, when it is one whose code the recruitment route can load ahead.
export const recruitmentPageInUrl = () =>
  /^\/(?:company\/[^/]+\/)?recruitment\/[^/]+\/(overview|applications\/all)\/?$/.exec(
    window.location.pathname,
  )?.[1];

// The same for the index route's pages, by their sidebar ids.
export const indexPageInUrl = () =>
  /^\/(?:company\/[^/]+\/)?(recruitments\/all-recruitments|applicants)\/?$/.exec(
    window.location.pathname,
  )?.[1];

// Only the default app tree has this start page, and the entry runs before it knows the user's company. It preloads
// unless the last load in this browser had another tree, so group, event-matching, free and reference companies
// don't download it every time.
const LAST_LOAD_HAD_START_PAGE = "lastLoadHadStartPage";

let lastLoadHadStartPage = true;

export function preloadStartPageFromEntry() {
  try {
    lastLoadHadStartPage =
      localStorage.getItem(LAST_LOAD_HAD_START_PAGE) !== "false";
    localStorage.setItem(LAST_LOAD_HAD_START_PAGE, "false");
  } catch {
    // Without storage, preload as for the default tree.
  }
  // The index route stays with the app route: importing it here would make Rolldown split the modules the two share
  // into about 20 more chunks, which every page would then load.
  if (lastLoadHadStartPage && opensStartPage()) {
    StartPageRoute.preload().catch(() => undefined);
  }
}

// Whether this load opens a page the app route starts loading as soon as it arrives, so that it needs no loading state
// from Suspense. Those pages wait for `Me` behind LoadingHome, then appear in the commit that answers it.
export const opensPreloadedPage = () =>
  lastLoadHadStartPage &&
  (opensStartPage() || !!recruitmentPageInUrl() || !!indexPageInUrl());

/** Rendered by the default app tree, so the next load preloads its start page. */
export function MarkStartPageTree() {
  useEffect(() => {
    try {
      localStorage.setItem(LAST_LOAD_HAD_START_PAGE, "true");
    } catch {
      // Without storage, every load preloads.
    }
  }, []);
  return null;
}
